import { FileKind, ModelAsset } from 'web';
import { v4 as uuidv4 } from 'uuid';
import { validateFile } from '@/utils/nodeInputValidation';
import { log } from '@/logger/logger';

export type AssetType = (MediaAsset | TextAsset)['type'];

const logger = log.getLogger('assets');

export type TextAsset = {
  id: string;
  type: 'text';
  input?: Record<string, string | number>;
  params?: Record<string, string | number>;
  value: string;
  originalValue?: string;
};

export type MediaAsset = {
  id: string;
  duration?: number;
  fps?: number;
  hasAudio?: boolean;
  height?: number;
  input?: Record<string, any>;
  name?: string;
  params?: Record<string, any>;
  publicId: string;
  thumbnailUrl: string;
  type: 'image' | 'video' | 'audio' | '3D';
  url: string;
  viewUrl: string;
  visualId: string;
  width?: number;
};

export type RenderingAsset = {
  type: 'rendering';
  progress?: number;
};

export type UploadedAsset = (TextAsset | MediaAsset | RenderingAsset) & {
  name?: string; // temp for design app output
  batchId?: string;
  order?: number;
  secondaryOrder?: number;
};

const validateInputInfo = (input: Record<string, any>) => {
  const array: [string, string][] = Array.from(
    Object.entries(input).map(([key, value]) => {
      if (value !== undefined && value !== null && typeof value === 'object' && 'input' in value) {
        value = value.input;
      }
      if (value !== undefined && value !== null && typeof value === 'object' && 'input_file' in value) {
        value = value.input_file;
      }
      if (value !== undefined && value !== null && typeof value === 'object' && 'image' in value) {
        value = value.image;
      }
      if (value !== undefined && value !== null && Array.isArray(value)) {
        if (value.every((item) => typeof item === 'string')) {
          return [key, value.filter((item) => item !== '').join('\n')];
        }
        return [key, JSON.stringify(value)];
      }
      if (value === undefined || value === null) {
        return [key, ''];
      }
      if (typeof value === 'object') {
        if ('url' in value && typeof value.url === 'string') {
          return [key, value.url];
        }
        if ('seed' in value && typeof value.seed !== 'object') {
          return [key, value.seed.toString()];
        }
        if ('value' in value && typeof value.value !== 'object') {
          return [key, value.value.toString()];
        }
        if ('prompt' in value && typeof value.prompt !== 'object') {
          return [key, value.prompt.toString()];
        }
        if (
          'prompt' in value &&
          typeof value.prompt === 'object' &&
          value.prompt !== null &&
          value.prompt !== undefined &&
          'value' in value.prompt &&
          typeof value.prompt.value === 'string'
        ) {
          return [key, value.prompt.value];
        }
        if (
          'width' in value &&
          typeof value.width === 'number' &&
          'height' in value &&
          typeof value.height === 'number'
        ) {
          return [key, `${value.width}x${value.height}`];
        }
        logger.error(`Unexpected input value for key: ${key}, value: ${JSON.stringify(value)}`);
      }
      return [key, value.toString()];
    }),
  );
  return array;
};

/**
 * @deprecated Use the `ModelAsset` type directly instead of the `UploadedAsset` type.
 */
export function UploadedAssetToModelAsset(asset: UploadedAsset): ModelAsset | undefined {
  switch (asset.type) {
    case 'text': {
      return {
        name: asset.name,
        batchId: asset.batchId,
        order: asset.order,
        secondaryOrder: asset.secondaryOrder,
        input: validateInputInfo(asset.input ?? {}),
        parameters: validateInputInfo(asset.params ?? {}),
        kind: { type: 'text', value: asset.value, originalValue: asset.originalValue, id: asset.id ?? uuidv4() },
      };
    }
    case 'image':
    case 'video':
    case 'audio':
    case '3D': {
      const mediaAsset = asset as MediaAsset;
      const kind: FileKind | undefined = validateFile(mediaAsset);
      if (!kind) {
        logger.error(`Invalid media asset: ${mediaAsset.type}`);
        return undefined;
      }

      return {
        name: asset.name,
        batchId: asset.batchId,
        order: asset.order,
        secondaryOrder: asset.secondaryOrder,
        input: validateInputInfo(asset.input ?? {}),
        parameters: validateInputInfo(asset.params ?? {}),
        kind,
      };
    }
    default:
      logger.error(`Unsupported asset type: ${asset.type}`);
  }
}

/**
 * @deprecated Use the `ModelAsset` type directly instead of the `UploadedAsset` type.
 */
export function ModelAssetToUploadedAsset(asset: ModelAsset): UploadedAsset {
  const common = {
    id: asset.kind.id ?? '',
    name: asset.name,
    batchId: asset.batchId,
    order: asset.order,
    secondaryOrder: asset.secondaryOrder,
    input: Object.fromEntries(asset.input.map((v) => [v[0], v[1].toString()])),
    params: Object.fromEntries(asset.parameters.map((v) => [v[0], v[1].toString()])),
  };

  switch (asset.kind.type) {
    case 'text':
      return {
        type: 'text',
        value: asset.kind.value,
        originalValue: asset.kind.originalValue,
        ...common,
      };
    case 'image':
      return {
        type: 'image',
        url: asset.kind.url,
        publicId: asset.kind.publicId ?? '',
        thumbnailUrl: asset.kind.thumbnailUrl ?? '',
        viewUrl: '',
        visualId: '',
        width: asset.kind.width,
        height: asset.kind.height,
        ...common,
      };
    case 'video':
      return {
        type: 'video',
        url: asset.kind.url,
        publicId: asset.kind.publicId ?? '',
        thumbnailUrl: asset.kind.thumbnailUrl ?? '',
        viewUrl: '',
        visualId: '',
        width: asset.kind.width,
        height: asset.kind.height,
        duration: asset.kind.duration,
        fps: asset.kind.fps,
        ...common,
      };
    case 'audio':
      return {
        type: 'audio',
        url: asset.kind.url,
        publicId: asset.kind.publicId ?? '',
        duration: asset.kind.duration,
        thumbnailUrl: '',
        viewUrl: '',
        visualId: '',
        ...common,
      };
    case '3D':
      return {
        type: '3D',
        url: asset.kind.url,
        publicId: asset.kind.publicId ?? '',
        thumbnailUrl: asset.kind.thumbnailUrl ?? '',
        viewUrl: '',
        visualId: '',
        ...common,
      };
  }
}
